import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { prayerApi } from '@/lib/api';
import { Heart, Check, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const AdminPrayerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const data = await prayerApi.getAll(false);
      setRequests(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item) => {
    try {
      await prayerApi.update(item.id, { is_approved: !item.is_approved });
      toast.success(item.is_approved ? 'Unapproved' : 'Approved');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleAnswered = async (item) => {
    try {
      await prayerApi.update(item.id, { is_answered: !item.is_answered });
      toast.success(item.is_answered ? 'Unmarked' : 'Marked as answered');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prayer request?')) return;
    try {
      await prayerApi.delete(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const pendingCount = requests.filter(r => !r.is_approved && r.is_public).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800">Prayer Requests</h1>
        <p className="text-stone-600">
          Manage prayer requests {pendingCount > 0 && <Badge className="ml-2 bg-amber-100 text-amber-700">{pendingCount} pending approval</Badge>}
        </p>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : requests.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="max-w-md">
                    <p className="line-clamp-2">{item.request}</p>
                  </TableCell>
                  <TableCell>
                    <div>{item.name}</div>
                    {item.email && <div className="text-xs text-stone-500">{item.email}</div>}
                  </TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {item.is_public && (
                        <Badge variant={item.is_approved ? "default" : "secondary"}>
                          {item.is_approved ? 'Public' : 'Pending'}
                        </Badge>
                      )}
                      {!item.is_public && <Badge variant="outline">Private</Badge>}
                      {item.is_answered && <Badge className="bg-green-100 text-green-700">Answered</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {item.is_public && (
                      <Button variant="ghost" size="sm" onClick={() => handleApprove(item)} title={item.is_approved ? 'Hide' : 'Approve'}>
                        {item.is_approved ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleAnswered(item)} title="Mark as answered" className={item.is_answered ? 'text-green-600' : ''}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <Heart className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Prayer Requests</h3>
          <p className="text-stone-600">Prayer requests will appear here when submitted.</p>
        </CardContent></Card>
      )}
    </div>
  );
};

export default AdminPrayerRequests;
